import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const navigate = useNavigate();
  const [showPricing, setShowPricing] = useState(false);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  // Subscription plans data
  const plans = [
    {
      name: 'Basic',
      price: 9.99,
      checks: 100,
      features: ['100 IMEI checks/month', 'Basic device info', 'Email support']
    },
    {
      name: 'Professional',
      price: 49.99,
      checks: 1000,
      popular: true,
      features: ['1,000 IMEI checks/month', 'Advanced device info', 'API access', 'Priority support']
    },
    {
      name: 'Enterprise',
      price: 199.99,
      checks: 10000,
      features: ['10,000 IMEI checks/month', 'Complete analytics', 'Custom integrations', 'Dedicated manager']
    }
  ];

  const handleCheckIMEI = () => {
    navigate('/auth/login');
  };

  const handleViewPricing = () => {
    setShowPricing(true);
  };

  return (
    <>
      <section id="home" className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-primary-50/20 py-8 lg:py-16 overflow-hidden flex items-center">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Left Content */}
            <motion.div className="text-center lg:text-left" variants={itemVariants}>
              <motion.div className="mb-6" variants={itemVariants}>
                <span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-4">
                  🚀 Professional IMEI Services
                </span>
              </motion.div>
              
              <motion.h1 
                className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
                variants={itemVariants}
              >
                DeviceLink Global
                <span className="block text-primary-600">IMEI Solutions</span>
              </motion.h1>
              
              <motion.p 
                className="text-lg lg:text-xl text-gray-600 mb-8 max-w-2xl leading-relaxed"
                variants={itemVariants}
              >
                Professional mobile device verification services for businesses worldwide. 
                Get instant access to comprehensive IMEI data, carrier information, and device status reports.
              </motion.p>

              {/* Key Benefits */}
              <motion.div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" variants={itemVariants}>
                <div className="text-center">
                  <span className="text-sm font-medium text-gray-700">Instant Results</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-medium text-gray-700">Secure & Reliable</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-medium text-gray-700">Global Coverage</span>
                </div>
              </motion.div>
              
              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 mb-8 justify-center lg:justify-start"
                variants={itemVariants}
              >
                <motion.button 
                  onClick={handleCheckIMEI}
                  className="bg-gradient-to-r from-primary-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Check IMEI
                </motion.button>
                <motion.button 
                  onClick={handleViewPricing}
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  View Pricing
                </motion.button>
              </motion.div>

              {/* Company Info */}
              <motion.div 
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-8 text-sm text-gray-500"
                variants={itemVariants}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Global Operations</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>24/7 Support</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>ISO Certified</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Content - Enhanced Visual */}
            <motion.div 
              className="relative"
              variants={itemVariants}
            >
              <motion.div 
                className="relative mx-auto w-80 h-96 lg:w-96 lg:h-[500px]"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
              >
                {/* Phone Frame */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-[3rem] shadow-2xl">
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-600 rounded-full"></div>
                  
                  {/* Screen Content */}
                  <motion.div 
                    className="absolute top-12 left-4 right-4 bottom-8 bg-white rounded-[2rem] overflow-hidden shadow-inner"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  >
                    {/* Status Bar */}
                    <div className="bg-success-500 text-white text-center py-2 text-sm font-semibold">
                      ✓ DeviceLink Global ✓ Verified ✓ Secure
                    </div>
                    
                    {/* IMEI Check Results */}
                    <div className="p-4 space-y-3">
                      <div className="bg-success-50 border border-success-200 rounded-lg p-3 shadow-sm">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-success-600 font-semibold text-xs">✓ Device Verified</span>
                          <span className="bg-success-500 text-white px-2 py-1 rounded text-xs font-medium">Clean</span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <div className="font-mono">358240051111110</div>
                          <div className="text-primary-600 font-medium">iPhone 15 Pro Max 256GB</div>
                          <div><span className="font-medium">Status:</span> Clean</div>
                          <div><span className="font-medium">Carrier:</span> Unlocked</div>
                          <div><span className="font-medium">Country:</span> United States</div>
                          <div><span className="font-medium">Warranty:</span> Active</div>
                          <div><span className="font-medium">Blacklisted:</span> No</div>
                          <div><span className="font-medium">Find My:</span> Off</div>
                        </div>
                      </div>
                      
                      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 shadow-sm">
                        <div className="text-xs text-gray-600 space-y-1">
                          <div><span className="font-medium">Model:</span> A2849</div>
                          <div><span className="font-medium">Color:</span> Natural Titanium</div>
                          <div><span className="font-medium">Storage:</span> 256GB</div>
                          <div><span className="font-medium">Network:</span> 5G Compatible</div>
                          <div><span className="font-medium">Activation:</span> Policy Verified</div>
                          <div><span className="font-medium">Simlock:</span> Factory Unlocked</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
                
                {/* Floating Elements */}
                <motion.div 
                  className="absolute -top-4 -right-4 w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white text-2xl shadow-lg"
                  animate={{ 
                    y: [0, -10, 0],
                    rotate: [0, 5, 0]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  📱
                </motion.div>
                <motion.div 
                  className="absolute -bottom-4 -left-4 w-12 h-12 bg-success-500 rounded-full flex items-center justify-center text-white shadow-lg"
                  animate={{ 
                    scale: [1, 1.1, 1],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  ✓
                </motion.div>
                <motion.div 
                  className="absolute top-1/4 -left-8 w-8 h-8 bg-warning-400 rounded-full flex items-center justify-center text-white text-sm"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{ 
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  🔒
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Statistics Section */}
          <motion.div 
            className="mt-16 lg:mt-24"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-3xl lg:text-4xl font-bold text-primary-600 mb-2">2.5M+</div>
                <div className="text-sm text-gray-600 font-medium">Devices Verified</div>
              </motion.div>
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-3xl lg:text-4xl font-bold text-success-600 mb-2">150+</div>
                <div className="text-sm text-gray-600 font-medium">Countries Covered</div>
              </motion.div>
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-3xl lg:text-4xl font-bold text-warning-600 mb-2">99.9%</div>
                <div className="text-sm text-gray-600 font-medium">Accuracy Rate</div>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5, scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-3xl lg:text-4xl font-bold text-error-600 mb-2">24/7</div>
                <div className="text-sm text-gray-600 font-medium">Support Available</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Modal */}
      <AnimatePresence>
        {showPricing && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPricing(false)} />
            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
              >
                <div className="p-8">
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-3xl font-bold text-gray-900">Choose Your Plan</h2>
                    <button
                      onClick={() => setShowPricing(false)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {plans.map((plan, index) => (
                      <motion.div
                        key={plan.name}
                        className={`relative p-6 rounded-2xl border-2 transition-all duration-300 ${
                          plan.popular 
                            ? 'border-primary-500 shadow-lg bg-primary-50' 
                            : 'border-gray-200 hover:border-primary-300'
                        }`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                      >
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                              Most Popular
                            </span>
                          </div>
                        )}

                        <div className="text-center mb-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                          <div className="text-3xl font-bold text-primary-600 mb-1">
                            ${plan.price}
                          </div>
                          <p className="text-gray-500">per month</p>
                          <p className="text-sm text-gray-600 mt-2">
                            {plan.checks.toLocaleString()} IMEI checks
                          </p>
                        </div>

                        <ul className="space-y-3 mb-6">
                          {plan.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start">
                              <svg className="w-5 h-5 text-success-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-gray-600 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={handleCheckIMEI}
                          className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                            plan.popular
                              ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          Get Started
                        </button>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-8 text-center">
                    <p className="text-gray-600">
                      Need a custom solution? 
                      <button className="text-primary-600 hover:text-primary-700 font-semibold ml-1">
                        Contact our sales team
                      </button>
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HeroSection;
